import { Add } from "@mui/icons-material";
import { Box, Button, Card, styled, useTheme, Grid, Tabs, Tab, Badge, alpha, useMediaQuery, CircularProgress } from "@mui/material";
import Agriculture from '@mui/icons-material/Agriculture';
import VideoCall from '@mui/icons-material/VideoCall';
import WhatsApp from '@mui/icons-material/WhatsApp';
import Schedule from '@mui/icons-material/Schedule';
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { H6 } from "components/Typography";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";
import { Typography } from "@mui/material";
import TalkToAgronomistList from "./agrolist";

const ButtonWrapper = styled(FlexBox)(({
  theme
}) => ({
  [theme.breakpoints.down(500)]: {
    marginTop: 10,
    width: "100%",
    flexDirection: "column-reverse",
    "& > .MuiBox-root": {
      width: "100%",
      margin: "10px 0",
      alignItems: "center",
      flexDirection: "column"
    },
    "& .MuiButton-root": {
      minWidth: "100%"
    }
  }
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  minHeight: 48,
  width: '100%',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTabs-flexContainer': {
    gap: 4,
    padding: 4,
  },
  '& .MuiTabs-scroller': {
    [theme.breakpoints.up('md')]: {
      '& .MuiTabs-flexContainer': {
        justifyContent: 'space-between',
      },
    },
  },
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 2px 8px rgba(0,0,0,0.3)' 
    : '0 2px 8px rgba(0,0,0,0.1)',
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  minHeight: 40,
  borderRadius: theme.shape.borderRadius,
  margin: 0,
  padding: '8px 16px',
  color: theme.palette.text.secondary,
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  
  // Desktop: Equal width distribution
  [theme.breakpoints.up('md')]: {
    flex: 1,
    minWidth: 0,
  },
  
  // Mobile: Minimum width for scrolling
  [theme.breakpoints.down('md')]: {
    minWidth: 120,
    padding: '8px 12px',
    fontSize: '0.8rem',
  },
  
  [theme.breakpoints.down('sm')]: {
    minWidth: 100,
    padding: '6px 8px',
    fontSize: '0.75rem',
  },

  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const getStatusColor = (theme, status) => {
  const colors = {
    all: theme.palette.text.primary,
    pending: theme.palette.warning.main,
    scheduled: theme.palette.info.main,
    connected: theme.palette.success.main,
    completed: theme.palette.success.main,
    cancelled: theme.palette.error.main,
  };
  return colors[status] || theme.palette.grey[500];
};

const TabContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  position: 'sticky',
  top: 0,
  zIndex: 10,
  backgroundColor: theme.palette.background.default,
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

const SummaryCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
}));

const TalkToAgronomist = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { setPageTitle } = usePageTitle();
  const [consultations, setConsultations] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    pending: 0,
    scheduled: 0,
    connected: 0,
    completed: 0,
    cancelled: 0,
  });
  const [totalSessions, setTotalSessions] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    next_page_number: null,
    previous_page_number: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];

  const statusTabs = [
    { value: 'pending', label: 'Pending', icon: '⏳' },
    { value: 'connected', label: 'Connected', icon: '🔗' },

  ];

  useEffect(() => {
    setPageTitle('Talk to Agronomist');
  }, [setPageTitle]);

  const fetchConsultations = async (page = 1, pageSizeParam = 10, status = statusFilter, search = searchValue) => {
    setIsLoading(true);
    try {
      const userId = localStorage.getItem("user_id");
      let url = `${API_ENDPOINTS.GET_talk_to_agronomist}?user_id=${userId}&page=${page}&page_size=${pageSizeParam}`;

      if (status && status !== 'all') {
        url += `&status=${encodeURIComponent(status)}`;
      }

      if (search && search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }

      const response = await apiService.get(url);
      
      if (response?.success) {
        let filteredData = response.data;
        setConsultations(Array.isArray(filteredData) ? filteredData : []);
        
        const paginationData = response?.pagination || response?.data?.pagination || {};
        if (Object.keys(paginationData).length > 0) {
          setPagination({
            current_page: paginationData.current_page ?? page,
            page_size: paginationData.page_size ?? pageSizeParam,
            total_items: paginationData.total_items ?? 0,
            total_pages: paginationData.total_pages ?? 1,
            has_next: paginationData.has_next ?? false,
            has_previous: paginationData.has_previous ?? false,
            next_page_number: paginationData.next_page_number ?? null,
            previous_page_number: paginationData.previous_page_number ?? null,
          });
          setCurrentPage(paginationData.current_page ?? page);
          setPageSize(paginationData.page_size ?? pageSizeParam);
        } else {
          const list = Array.isArray(response.data) ? response.data : [];
          setPagination(prev => ({
            ...prev,
            total_items: list.length,
            total_pages: 1,
            current_page: 1,
            page_size: pageSizeParam,
          }));
        }
        
        const dataList = Array.isArray(response.data) ? response.data : [];
        const counts = {
          all: dataList.length,
          pending: dataList.filter(item => !item.connected && !item.status).length,
          scheduled: dataList.filter(item => !item.connected && item.date && item.time).length,
          connected: dataList.filter(item => item.connected === true).length,
          completed: dataList.filter(item => item.status === 'completed').length,
          cancelled: dataList.filter(item => item.status === 'cancelled').length,
        };
        setStatusCounts(counts);
      }
    } catch (error) {
      console.error("Error fetching consultations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch dashboard data for total sessions
  const fetchDashboardData = async () => {
    setLoadingDashboard(true);
    try {
      const response = await apiService.get(API_ENDPOINTS.GET_talk_to_agronomist_dashboard);
      if (response?.success && response?.data?.summary) {
        setTotalSessions(response.data.summary.total_sessions);
      } else {
        setTotalSessions(null);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setTotalSessions(null);
    } finally {
      setLoadingDashboard(false);
    }
  };

  useEffect(() => {
    fetchConsultations(1, pageSize, statusFilter, searchValue);
    fetchDashboardData();
  }, []);



  const handleRefresh = (status = statusFilter, search = searchValue) => {
    setStatusFilter(status);
    setSearchValue(search);
    setCurrentPage(1);
    fetchConsultations(1, pageSize, status, search);
  };

  const handlePageChange = (_, value) => {
    setCurrentPage(value);
    fetchConsultations(value, pageSize, statusFilter, searchValue);
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    fetchConsultations(1, newSize, statusFilter, searchValue);
  };

  return (
    <Box pt={2} pb={4}>
      <Card
        sx={{
          height: '100px',
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
        }}
      >
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={8}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
            Agronomist Consultations
            </Typography>
            <Typography variant="body2" color="text.secondary">
            Manage and track farmer consultation requests</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <SummaryCard sx={{ height: '50px' }}>
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                <span style={{ fontWeight: 'bold' }}>Total Sessions:</span> {totalSessions || 0}
              </Typography>
            </SummaryCard>
          </Grid>
        </Grid>
      </Card>

      {/* Consultations Table */}
      <Card sx={{ marginTop: 2, borderRadius: 3, border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
        
        <TalkToAgronomistList
          data={consultations} 
          searchQuery={searchValue} 
          onRefresh={(status, search) => {
            handleRefresh(status, search);
            fetchDashboardData(); // Refresh dashboard data on manual refresh
          }}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={pageSizeOptions}
        />
      </Card>
    </Box>
  );
};

export default TalkToAgronomist;